import { useQuery } from "react-query";
import { QueryKeys } from "./query-client";
import { BACKEND_URL } from "../../constants/env";
import { CustomerAttachment } from "../../constants/model/models";
import { useJWT } from "./useJWT";
import { useAlertStore } from "../../components/config/store/alertStore";
import { useTranslation } from "next-i18next";
import { useProcessingStore } from "../../components/config/store/usProccessingStore";
import axios from "axios";

export function useAttachmentList() {
  const { jwt } = useJWT();
  const { setProcessing } = useProcessingStore((state) => state);
  const { setAlert } = useAlertStore();
  const { t } = useTranslation("common");

  async function getAttachmentList(): Promise<CustomerAttachment[]> {
    if (jwt) {
      return fetchAttachments(jwt);
    } else {
      return [];
    }
  }

  async function fetchAttachments(authToken: string) {
    const url = `${BACKEND_URL}/user/profile/attachments`;

    const response = await fetch(url, {
      headers: {
        "Content-type": "application/json",
        accessToken: authToken,
      },
    });

    return await response.json();
  }

  const {
    data: attachmentData,
    isFetching: attachmentsAreLoading,
    isError: attachmentListError,
  } = useQuery([QueryKeys.ATTACHMENTS], () => getAttachmentList(), {
    keepPreviousData: true,
    enabled: jwt !== null && typeof jwt !== "undefined",
    retry: 2,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  });

  function getCategoryList(attachments: CustomerAttachment[]) {
    const categories: string[] = [];
    if (attachments && attachments.length > 0) {
      attachments.forEach((attachment) => {
        if (attachment.category) {
          if (attachment.category.length > 0) {
            if (!categories.includes(attachment.category))
              categories.push(attachment.category);
          } else {
            if (!categories.includes("###")) categories.push("###");
          }
        } else {
          if (!categories.includes("###")) categories.push("###");
        }
      });
    }

    return categories;
  }

  function getCategoryAttachments(
    attachments: CustomerAttachment[],
    category: string
  ): CustomerAttachment[] {
    if (category !== "###") {
      return attachments.filter((att) => att.category === category);
    } else {
      return attachments.filter(
        (att) => att.category === null || att.category.length === 0
      );
    }
  }

  function getFileName(attachment: CustomerAttachment) {
    if (attachment.name.length > 0) {
      return attachment.name;
    } else {
      return attachment.link?.split(`customer-id-`)[1];
    }
  }

  async function downloadAttachment(attachment: CustomerAttachment) {
    setProcessing(true);
    try {
      if (attachment.link && jwt) {
        const key = getFileKey(attachment.link);
        const response = await getCustomerAttachment(attachment.id, jwt);

        if (response && response.status === 200) {
          // Create blob link to download
          const blob = await response.data;
          const url = window.URL.createObjectURL(new Blob([blob]));

          const link = document.createElement("a");
          link.href = url;

          link.setAttribute(
            "download",
            response.headers["x-suggested-filename"] || ""
          );

          // Append to html link element page
          document.body.appendChild(link);

          // Start download
          link.click();

          // Clean up and remove the link
          if (link.parentNode) link.parentNode.removeChild(link);
          setProcessing(false);
        }
      } else {
        setAlert(t("downloadError"));
        setProcessing(false);
      }
    } catch (err) {
      console.error(err);
      setAlert(t("downloadError"));
      setProcessing(false);
    }
  }

  async function getCustomerAttachment(attachmentId: number, jwt: string) {
    // const response = await fetch(
    //   `${BACKEND_URL}/user/profile/attachments/${attachmentId}/download`,
    //   {
    //     method: "GET",
    //     headers: {
    //       responseType: "blob",
    //       accessToken: jwt,
    //     },
    //   }
    // );
    //
    // return response;

    return await axios.get(
      `${BACKEND_URL}/user/profile/attachments/${attachmentId}/download`,
      {
        responseType: "blob",
        headers: {
          accessToken: jwt,
          // Accept: "application/pdf",
          // "Content-type": "application/pdf",
        },
      }
    );
  }

  function getFileKey(link: string) {
    return link.split(
      "https://bookanapp-provider-customer-attachment.s3.eu-west-3.amazonaws.com/"
    )[1];
  }

  return {
    attachmentData,
    attachmentsAreLoading,
    attachmentListError,
    getCategoryAttachments,
    getCategoryList,
    getFileName,
    downloadAttachment,
  };
}
